/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  SupportAccessReasonDialog,
  type SupportAccessReason,
  type SupportAccessReasonCode,
} from './SupportAccessReasonDialog'

export interface BreakGlassSessionLike {
  reason: string
  grantId?: string
  ticketId?: string
  linkedId?: string
  revealToken?: string
}

export interface EnsureAccessReasonOptions {
  key: string
  defaultCode?: SupportAccessReasonCode
  defaultTicketId?: string
  onCancel?: () => void
}

export interface SupportAccessReasonContextValue {
  ensureAccessReason: (options: EnsureAccessReasonOptions) => Promise<SupportAccessReason | null>
  getCachedReason: (key: string) => SupportAccessReason | undefined
}

const DEFAULT_CONTEXT_VALUE: SupportAccessReasonContextValue = {
  ensureAccessReason: async (opts) => ({
    code: opts.defaultCode || 'ROUTINE_REVIEW',
    ticketId: opts.defaultTicketId || 'DEFAULT',
  }),
  getCachedReason: () => undefined,
}

const SupportAccessReasonContext = createContext<SupportAccessReasonContextValue>(DEFAULT_CONTEXT_VALUE)

interface DialogState {
  key: string
  defaultCode?: SupportAccessReasonCode
  defaultTicketId?: string
  resolvers: Array<(reason: SupportAccessReason | null) => void>
  cancels: Array<(() => void) | undefined>
}

export function SupportAccessReasonProvider({
  children,
  activeBreakGlass,
}: {
  children: ReactNode
  activeBreakGlass?: BreakGlassSessionLike | null
}) {
  const cacheRef = useRef<Map<string, SupportAccessReason>>(new Map())
  const [dialogState, setDialogState] = useState<DialogState | null>(null)
  const dialogStateRef = useRef<DialogState | null>(null)
  useEffect(() => {
    dialogStateRef.current = dialogState
  }, [dialogState])

  const getCachedReason = useCallback((key: string): SupportAccessReason | undefined => {
    return cacheRef.current.get(key)
  }, [])

  const ensureAccessReason = useCallback(
    async (options: EnsureAccessReasonOptions): Promise<SupportAccessReason | null> => {
      const { key, defaultCode = 'SUPPORT_TICKET', defaultTicketId = '', onCancel } = options

      // Active break-glass grant: bypass dialog and use grant's reason and ticket
      if (activeBreakGlass) {
        const bgReason: SupportAccessReason = {
          code: (activeBreakGlass.reason as SupportAccessReasonCode) || 'SUPPORT_TICKET',
          ticketId: activeBreakGlass.ticketId || activeBreakGlass.linkedId || 'BREAK_GLASS',
          ...(activeBreakGlass.linkedId ? { linkedId: activeBreakGlass.linkedId } : {}),
        }
        cacheRef.current.set(key, bgReason)
        return bgReason
      }

      // Existing cached answer for this customer in this console session
      const cached = cacheRef.current.get(key)
      if (cached) {
        return cached
      }

      // Prompt operator - if already prompt pending for this key, join the pending resolvers
      return new Promise<SupportAccessReason | null>((resolve) => {
        setDialogState((prev) => {
          if (prev && prev.key === key) {
            return {
              ...prev,
              defaultTicketId: prev.defaultTicketId || defaultTicketId,
              resolvers: [...prev.resolvers, resolve],
              cancels: [...prev.cancels, onCancel],
            }
          }
          return {
            key,
            defaultCode,
            defaultTicketId,
            resolvers: [resolve],
            cancels: [onCancel],
          }
        })
      })
    },
    [activeBreakGlass],
  )

  const handleDialogSubmit = useCallback((reason: SupportAccessReason) => {
    const current = dialogStateRef.current
    if (current) {
      cacheRef.current.set(current.key, reason)
      current.resolvers.forEach((res) => res(reason))
      setDialogState(null)
    }
  }, [])

  const handleDialogCancel = useCallback(() => {
    const current = dialogStateRef.current
    if (current) {
      current.cancels.forEach((c) => c?.())
      current.resolvers.forEach((res) => res(null))
      setDialogState(null)
    }
  }, [])

  const value: SupportAccessReasonContextValue = {
    ensureAccessReason,
    getCachedReason,
  }

  return (
    <SupportAccessReasonContext.Provider value={value}>
      {children}
      {dialogState && (
        <SupportAccessReasonDialog
          open={true}
          defaultCode={dialogState.defaultCode}
          defaultTicketId={dialogState.defaultTicketId}
          onSubmit={handleDialogSubmit}
          onCancel={handleDialogCancel}
        />
      )}
    </SupportAccessReasonContext.Provider>
  )
}

export function useSupportAccessReason(): SupportAccessReasonContextValue {
  const ctx = useContext(SupportAccessReasonContext)
  if (!ctx) {
    // Graceful fallback for tests without provider: return default prompt resolver
    return {
      ensureAccessReason: async (opts) => ({
        code: opts.defaultCode || 'SUPPORT_TICKET',
        ticketId: opts.defaultTicketId || 'FALLBACK-TKT',
      }),
      getCachedReason: () => undefined,
    }
  }
  return ctx
}
